// Part of accountingPages, split by screen to keep files short; ../accountingPages.ts merges the parts.
// Keys sit directly under accountingPages, in sub-objects whose names no other part uses.
export const en = {
  assetDetailMore: {
    comma: ', ',
    disposedToast: '{{number}} disposed of.',
  },
  vatCsv: {
    trnLine: 'TRN {{trn}}',
    title: 'VAT return {{label}} ({{from}} to {{to}})',
    box: 'Box',
    description: 'Description',
    amountIn: 'Amount ({{currency}})',
    vatIn: 'VAT ({{currency}})',
    adjAmount: 'Adjustment amount',
    adjVat: 'Adjustment VAT',
    totalAmount: 'Total amount',
    totalVat: 'Total VAT',
    adjustments: 'Adjustments',
    amount: 'Amount',
    vat: 'VAT',
    reason: 'Reason',
    by: 'By',
  },
  currencyName: {
    INR: 'Indian rupee',
    AED: 'UAE dirham',
  },
  /** Seeded VAT/GST code descriptions, keyed by code: shown in Arabic only while a code still has this exact text. */
  taxCodeDescription: {
    SR: 'Standard-rated supplies and purchases. Sales go to boxes 1a–1g by emirate; purchases to box 9.',
    ZR: 'Zero-rated supplies such as exports and international transport (box 4).',
    EX: 'Exempt supplies such as residential rent and some financial services (box 5). No input VAT recovery.',
    OS: 'Outside UAE VAT: salaries, government fees, dividends. Not reported on the return.',
    RC: 'Imported services and goods under the reverse charge: output VAT in box 3, input VAT in box 10.',
    IMP: 'Goods imported through UAE customs (box 6).',
    'SR-BL':
      'Standard-rated purchases whose input VAT cannot be recovered (blocked: entertainment, private use of vehicles, staff benefits; or the non-recoverable share of an apportioned purchase). The VAT is part of the cost and is not reported on the return.',
    GST5: 'Intra-state supplies at 5%: CGST 2.5% and SGST 2.5%.',
    GST12: 'Intra-state supplies at 12%: CGST 6% and SGST 6%. For supplies up to 21/09/2025.',
    GST18: 'Intra-state supplies at 18%: CGST 9% and SGST 9%.',
    GST28: 'Intra-state supplies at 28%: CGST 14% and SGST 14%. For supplies up to 21/09/2025.',
    GST40:
      'Intra-state supplies at 40% (aerated drinks, large cars, motorcycles above 350 cc and other listed goods): CGST 20% and SGST 20%. From 22/09/2025.',
    IGST5: 'Inter-state supplies at 5%.',
    IGST12: 'Inter-state supplies at 12%. For supplies up to 21/09/2025.',
    IGST18: 'Inter-state supplies at 18%.',
    IGST28: 'Inter-state supplies at 28%. For supplies up to 21/09/2025.',
    IGST40: 'Inter-state supplies at 40%. From 22/09/2025.',
    NIL: 'Goods and services taxed at 0% (fresh produce, books). GSTR-1 table 8, GSTR-3B 3.1(c).',
    EXEMPT: 'Exempt supplies (healthcare, education, residential rent). No input tax credit.',
    'NON-GST':
      'Outside GST: petroleum products, alcohol for human consumption, salaries, government fees.',
    'EXP-LUT':
      'Exports of goods or services without payment of IGST under a letter of undertaking. GSTR-1 table 6A, GSTR-3B 3.1(b).',
    RCM5: 'Intra-state purchases on which you pay the GST (goods transport agency, legal services): CGST and SGST.',
    RCM18: 'Intra-state purchases on which you pay the GST at 18%: CGST and SGST.',
    RCMI5: 'Inter-state purchases on which you pay the GST at 5%: IGST.',
    RCMI18: 'Inter-state purchases and imports of services on which you pay IGST at 18%.',
    'GST-BL':
      'Purchases whose input tax credit is blocked (section 17(5): food, club membership, personal use). The GST is part of the cost and is not reported.',
  },
  /** The same, as companies set up before the September 2025 GST rate change still have them. */
  taxCodeDescriptionBefore2025: {
    GST12: 'Intra-state supplies at 12%: CGST 6% and SGST 6%.',
    GST28: 'Intra-state supplies at 28%: CGST 14% and SGST 14%.',
    IGST12: 'Inter-state supplies at 12%.',
    IGST28: 'Inter-state supplies at 28%.',
  },
};

export const ar = {
  assetDetailMore: {
    comma: '، ',
    disposedToast: 'تم استبعاد {{number}}.',
  },
  vatCsv: {
    trnLine: 'TRN {{trn}}',
    title: 'إقرار ضريبة القيمة المضافة {{label}} (من {{from}} إلى {{to}})',
    box: 'الخانة',
    description: 'الوصف',
    amountIn: 'المبلغ ({{currency}})',
    vatIn: 'ضريبة القيمة المضافة ({{currency}})',
    adjAmount: 'مبلغ التعديل',
    adjVat: 'ضريبة القيمة المضافة على التعديل',
    totalAmount: 'إجمالي المبلغ',
    totalVat: 'إجمالي ضريبة القيمة المضافة',
    adjustments: 'التعديلات',
    amount: 'المبلغ',
    vat: 'ضريبة القيمة المضافة',
    reason: 'السبب',
    by: 'بواسطة',
  },
  currencyName: {
    INR: 'الروبية الهندية',
    AED: 'الدرهم الإماراتي',
  },
  taxCodeDescription: {
    SR: 'التوريدات والمشتريات الخاضعة للنسبة الأساسية. تُدرج المبيعات في الخانات 1a–1g حسب الإمارة، والمشتريات في الخانة 9.',
    ZR: 'التوريدات الخاضعة لنسبة الصفر مثل الصادرات والنقل الدولي (الخانة 4).',
    EX: 'التوريدات المعفاة مثل الإيجار السكني وبعض الخدمات المالية (الخانة 5). لا يُسترد عنها ضريبة المدخلات.',
    OS: 'خارج نطاق ضريبة القيمة المضافة في الإمارات: الرواتب والرسوم الحكومية وتوزيعات الأرباح. لا تُدرج في الإقرار الضريبي.',
    RC: 'الخدمات والسلع المستوردة الخاضعة لآلية الاحتساب العكسي: ضريبة المخرجات في الخانة 3، وضريبة المدخلات في الخانة 10.',
    IMP: 'البضائع المستوردة عبر الجمارك الإماراتية (الخانة 6).',
    'SR-BL':
      'المشتريات الخاضعة للنسبة الأساسية التي لا يمكن استرداد ضريبة مدخلاتها (محظورة: الترفيه، والاستخدام الخاص للمركبات، ومزايا الموظفين؛ أو الحصة غير القابلة للاسترداد من مشتريات موزعة نسبياً). تُعد الضريبة جزءاً من التكلفة ولا تُدرج في الإقرار الضريبي.',
    GST5: 'التوريدات داخل الولاية بنسبة 5%: CGST 2.5% وSGST 2.5%.',
    GST12: 'التوريدات داخل الولاية بنسبة 12%: CGST 6% وSGST 6%. للتوريدات حتى 21/09/2025.',
    GST18: 'التوريدات داخل الولاية بنسبة 18%: CGST 9% وSGST 9%.',
    GST28: 'التوريدات داخل الولاية بنسبة 28%: CGST 14% وSGST 14%. للتوريدات حتى 21/09/2025.',
    GST40:
      'التوريدات داخل الولاية بنسبة 40% (المشروبات الغازية والسيارات الكبيرة والدراجات النارية فوق 350 سم مكعب وسلع أخرى مدرجة): CGST 20% وSGST 20%. اعتباراً من 22/09/2025.',
    IGST5: 'التوريدات بين الولايات بنسبة 5%.',
    IGST12: 'التوريدات بين الولايات بنسبة 12%. للتوريدات حتى 21/09/2025.',
    IGST18: 'التوريدات بين الولايات بنسبة 18%.',
    IGST28: 'التوريدات بين الولايات بنسبة 28%. للتوريدات حتى 21/09/2025.',
    IGST40: 'التوريدات بين الولايات بنسبة 40%. اعتباراً من 22/09/2025.',
    NIL: 'السلع والخدمات الخاضعة لنسبة 0% (المنتجات الطازجة والكتب). GSTR-1 الجدول 8، GSTR-3B 3.1(c).',
    EXEMPT:
      'التوريدات المعفاة (الرعاية الصحية والتعليم والإيجار السكني). لا يُسترد عنها رصيد ضريبة المدخلات.',
    'NON-GST':
      'خارج نطاق ضريبة السلع والخدمات: المنتجات البترولية والكحول للاستهلاك البشري والرواتب والرسوم الحكومية.',
    'EXP-LUT':
      'صادرات السلع أو الخدمات دون سداد IGST بموجب خطاب تعهد (LUT). GSTR-1 الجدول 6A، GSTR-3B 3.1(b).',
    RCM5: 'المشتريات داخل الولاية التي تسدد عنها ضريبة السلع والخدمات بنفسك (وكالات نقل البضائع، الخدمات القانونية): CGST وSGST.',
    RCM18: 'المشتريات داخل الولاية التي تسدد عنها ضريبة السلع والخدمات بنسبة 18%: CGST وSGST.',
    RCMI5: 'المشتريات بين الولايات التي تسدد عنها ضريبة السلع والخدمات بنسبة 5%: IGST.',
    RCMI18: 'المشتريات بين الولايات واستيراد الخدمات التي تسدد عنها IGST بنسبة 18%.',
    'GST-BL':
      'المشتريات المحظور استرداد رصيد ضريبة مدخلاتها (المادة 17(5): الطعام، وعضوية الأندية، والاستخدام الشخصي). تُعد ضريبة السلع والخدمات جزءاً من التكلفة ولا يُبلَّغ عنها.',
  },
  taxCodeDescriptionBefore2025: {
    GST12: 'التوريدات داخل الولاية بنسبة 12%: CGST 6% وSGST 6%.',
    GST28: 'التوريدات داخل الولاية بنسبة 28%: CGST 14% وSGST 14%.',
    IGST12: 'التوريدات بين الولايات بنسبة 12%.',
    IGST28: 'التوريدات بين الولايات بنسبة 28%.',
  },
};
